import { createInvoicesViewConfig } from "@common/helpers/close-template-task-view-config";
import { omit } from "@common/helpers/objects";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { FiltersTypes } from "@common/types/filters/general";
import { Invoice } from "@common/types/invoice";
import {
    InvoiceColumn,
    InvoiceView,
    InvoiceViewSchema,
} from "@common/types/views/invoiceViews";
import { PersistableView, ViewDensity } from "@common/types/views/views";
import { parseTaskView, withBulkActions } from "../shared/taskTableView";

export function defaultInvoicesView(): InvoiceView {
    return InvoiceViewSchema.parse(createInvoicesViewConfig());
}

/** Parses a persisted task or template view, falling back to the default. */
export function parseInvoicesTaskView(
    raw: object | null | undefined,
): InvoiceView {
    return parseTaskView({
        schema: InvoiceViewSchema,
        raw,
        createDefaultView: defaultInvoicesView,
    });
}

/** Keys that identify an invoices task's scope, not matching criteria. */
export const INVOICES_TASK_SCOPE_KEYS = ["entityIds", "invoiceIds"] as const;

export function omitInvoicesTaskScope(filters: FiltersTypes): FiltersTypes {
    return omit(filters, [...INVOICES_TASK_SCOPE_KEYS]);
}

/**
 * Strips apply-time-only fields so a template stays workspace agnostic: the
 * entity and pinned ids are injected when it is applied.
 */
export function serializeInvoicesTemplateView(
    view: InvoiceView,
): CloseTemplateViewConfig {
    return { ...view, filters: omitInvoicesTaskScope(view.filters) };
}

/** Columns that only make sense on the invoices page, not in a task. */
const NON_TASK_COLUMNS: ReadonlySet<string> = new Set([
    InvoiceColumn.SELECT,
    InvoiceColumn.SCHEDULE,
]);

interface BuildBulkTaskViewParams {
    view: InvoiceView;
    invoices: Array<Pick<Invoice, "id" | "entityId">>;
}

/**
 * View of a task created from an invoices selection: the list's current
 * columns, the entities of the selected invoices and exactly those invoices.
 * The list's own filters and sticky criteria are dropped: the pinned ids
 * decide membership.
 */
export function buildBulkInvoicesTaskView({
    view,
    invoices,
}: BuildBulkTaskViewParams): PersistableView {
    return withBulkActions({
        ...omit(view, ["originalFilters"]),
        options: { ...view.options, density: ViewDensity.MD },
        columns: view.columns.filter(
            (column) => !NON_TASK_COLUMNS.has(column.id),
        ),
        filters: {
            entityIds: [
                ...new Set(invoices.map((invoice) => invoice.entityId)),
            ],
            invoiceIds: invoices.map((invoice) => invoice.id),
        },
    });
}
